import { Events } from '../events';
import { opFromModifiers } from '../select-op';

class BrushSelection {
    activate: () => void;
    deactivate: () => void;

    constructor(events: Events, parent: HTMLElement, mask: { canvas: HTMLCanvasElement, context: CanvasRenderingContext2D, busy: boolean }) {
        // create svg
        const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        svg.classList.add('tool-svg', 'hidden');
        svg.id = 'brush-select-svg';
        parent.appendChild(svg);

        // create circle element
        const circle = document.createElementNS(svg.namespaceURI, 'circle') as SVGCircleElement;
        svg.appendChild(circle);

        const { canvas, context } = mask;

        let radius = 40;

        circle.setAttribute('r', radius.toString());

        const prev = { x: 0, y: 0 };
        let dragId: number | undefined;

        // track the pointer while the tool is inactive too (the tools overlay is
        // hidden then), so activation places the cursor at the mouse rather than
        // wherever the previous stroke ended
        const pointer = { x: 0, y: 0 };
        window.addEventListener('pointermove', (e: PointerEvent) => {
            pointer.x = e.clientX;
            pointer.y = e.clientY;
        }, { capture: true, passive: true });

        const update = (e: PointerEvent) => {
            const x = e.offsetX;
            const y = e.offsetY;

            circle.setAttribute('cx', x.toString());
            circle.setAttribute('cy', y.toString());

            if (dragId !== undefined) {
                context.beginPath();
                context.strokeStyle = '#f60';
                context.lineCap = 'round';
                context.lineWidth = radius * 2;
                context.moveTo(prev.x, prev.y);
                context.lineTo(x, y);
                context.stroke();

                prev.x = x;
                prev.y = y;
            }
        };

        const pointerdown = (e: PointerEvent) => {
            if (dragId === undefined && (e.pointerType === 'mouse' ? e.button === 0 : e.isPrimary)) {
                e.preventDefault();
                e.stopPropagation();

                // a stroke attempted while the previous selection is still
                // pending is swallowed rather than left to orbit the camera
                if (mask.busy) {
                    return;
                }

                dragId = e.pointerId;
                parent.setPointerCapture(dragId);

                // initialize canvas
                if (canvas.width !== parent.clientWidth || canvas.height !== parent.clientHeight) {
                    canvas.width = parent.clientWidth;
                    canvas.height = parent.clientHeight;
                }

                // clear canvas
                context.clearRect(0, 0, canvas.width, canvas.height);

                // display it
                canvas.style.display = 'inline';

                prev.x = e.offsetX;
                prev.y = e.offsetY;

                // a click has no pointermove, and the zero-length stroke that
                // update() draws for it leaves no mark in safari, so paint the
                // starting dot explicitly
                context.beginPath();
                context.fillStyle = '#f60';
                context.arc(prev.x, prev.y, radius, 0, Math.PI * 2);
                context.fill();

                update(e);
            }
        };

        const pointermove = (e: PointerEvent) => {
            if (dragId !== undefined) {
                e.preventDefault();
                e.stopPropagation();
            }

            update(e);
        };

        const dragEnd = () => {
            // a touch that has lifted, or was cancelled, no longer holds the
            // capture and releasing it throws
            if (parent.hasPointerCapture(dragId)) {
                parent.releasePointerCapture(dragId);
            }
            dragId = undefined;
            canvas.style.display = 'none';
        };

        const pointerup = async (e: PointerEvent) => {
            if (e.pointerId === dragId) {
                e.preventDefault();
                e.stopPropagation();

                dragEnd();

                // the selection snapshots the canvas synchronously when
                // invoked, so busy is UX policy rather than correctness: it
                // keeps strokes from piling up invisibly behind a slow
                // selection (pointerdown swallows them)
                mask.busy = true;
                try {
                    await events.invoke(
                        'select.byMask',
                        opFromModifiers(e),
                        canvas,
                        context
                    );
                } finally {
                    mask.busy = false;
                }
            }
        };

        // a cancelled touch gets no pointerup, and a drag left open blocks
        // every later one
        const pointercancel = (e: PointerEvent) => {
            if (e.pointerId === dragId) {
                dragEnd();
            }
        };

        const wheel = (e: WheelEvent) => {
            if (e.altKey || e.metaKey) {
                const { deltaX, deltaY } = e;
                events.fire((Math.abs(deltaX) > Math.abs(deltaY) ? deltaX : deltaY) > 0 ? 'tool.brushSelection.smaller' : 'tool.brushSelection.bigger');
                e.preventDefault();
                e.stopPropagation();
            }
        };

        this.activate = () => {
            svg.classList.remove('hidden');
            parent.style.display = 'block';
            const rect = parent.getBoundingClientRect();
            circle.setAttribute('cx', (pointer.x - rect.left).toString());
            circle.setAttribute('cy', (pointer.y - rect.top).toString());
            parent.addEventListener('pointerdown', pointerdown);
            parent.addEventListener('pointermove', pointermove);
            parent.addEventListener('pointerup', pointerup);
            parent.addEventListener('pointercancel', pointercancel);
            parent.addEventListener('wheel', wheel);
        };

        this.deactivate = () => {
            // cancel active operation
            if (dragId !== undefined) {
                dragEnd();
            }
            svg.classList.add('hidden');
            parent.style.display = 'none';
            parent.removeEventListener('pointerdown', pointerdown);
            parent.removeEventListener('pointermove', pointermove);
            parent.removeEventListener('pointerup', pointerup);
            parent.removeEventListener('pointercancel', pointercancel);
            parent.removeEventListener('wheel', wheel);
        };

        events.on('tool.brushSelection.smaller', () => {
            radius = Math.max(1, radius / 1.05);
            circle.setAttribute('r', radius.toString());
        });

        events.on('tool.brushSelection.bigger', () => {
            radius = Math.min(500, radius * 1.05);
            circle.setAttribute('r', radius.toString());
        });
    }
}

export { BrushSelection };
